/**
 * Radius + visit-status visibility for voter markers.
 */

import { isShowAllView } from './config.js';
import { map, theMarkers, activeVisitStatus, radiusCircle } from './state.js';
import { prefetchVisibleMarkerUrls } from './prefetch.js';

/**
 * Normalizes an Advanced Marker position to a lat/lng literal.
 *
 * @param {google.maps.marker.AdvancedMarkerElement} marker Marker instance.
 * @return {google.maps.LatLngLiteral|null} Lat/lng literal, or null if missing.
 */
function getMarkerLatLng( marker ) {
	const position = marker.position;
	if ( ! position ) {
		return null;
	}

	if ( typeof position.lat === 'function' && typeof position.lng === 'function' ) {
		return { lat: position.lat(), lng: position.lng() };
	}

	return {
		lat: Number( position.lat ),
		lng: Number( position.lng ),
	};
}

/**
 * Returns great-circle distance in meters between two lat/lng points.
 *
 * @param {google.maps.LatLngLiteral} a First point.
 * @param {google.maps.LatLngLiteral} b Second point.
 * @return {number} Distance in meters.
 */
function distanceMeters( a, b ) {
	const earthRadius = 6371000;
	const toRad = ( deg ) => ( deg * Math.PI ) / 180;
	const dLat = toRad( b.lat - a.lat );
	const dLng = toRad( b.lng - a.lng );
	const lat1 = toRad( a.lat );
	const lat2 = toRad( b.lat );
	const h =
		Math.sin( dLat / 2 ) ** 2 +
		Math.cos( lat1 ) * Math.cos( lat2 ) * Math.sin( dLng / 2 ) ** 2;

	return 2 * earthRadius * Math.asin( Math.sqrt( h ) );
}

/**
 * Whether a lat/lng falls inside the current my-location radius circle.
 *
 * @param {google.maps.LatLngLiteral} latLng Point to test.
 * @return {boolean} True when the point is within the circle radius.
 */
function isInsideLocationRadius( latLng ) {
	if ( ! radiusCircle ) {
		return false;
	}

	const center = radiusCircle.getCenter();
	if ( ! center ) {
		return false;
	}

	const centerLiteral = {
		lat: center.lat(),
		lng: center.lng(),
	};

	return distanceMeters( centerLiteral, latLng ) <= radiusCircle.getRadius();
}

/**
 * Updates which voter markers are shown based on location radius and filter.
 *
 * Markers are hidden until a my-location radius exists, when outside that
 * radius, or when excluded by the active visit-status filter.
 * When the URL has `view=showAll`, the radius restriction is skipped.
 *
 * @return {void}
 */
export function updateMarkerVisibility() {
	if ( ! map ) {
		return;
	}

	const showAll = isShowAllView();

	for ( const status of Object.keys( theMarkers ) ) {
		const group = theMarkers[ status ];
		const statusAllowed =
			'all' === activeVisitStatus || status === activeVisitStatus;

		for ( let i = 0; i < group.length; i++ ) {
			const marker = group[ i ];
			let visible = false;

			if ( statusAllowed ) {
				if ( showAll ) {
					visible = true;
				} else {
					const latLng = getMarkerLatLng( marker );
					visible = !!( latLng && isInsideLocationRadius( latLng ) );
				}
			}

			marker.map = visible ? map : null;
		}
	}

	prefetchVisibleMarkerUrls();
}
